/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  buildHotelBannerViewAnalytics,
  buildHotelBannerTapAnalytics,
  buildSearchWidgetViewAnalytics,
  buildSmartFillInputAnalytics,
  buildSmartFillResultsAnalytics,
  buildSuggestionClickAnalytics,
  buildPopularSearchClickAnalytics,
  buildPopularSearchViewedAnalytics,
  buildNoResultsAnalytics,
  buildSearchButtonClickAnalytics,
  buildCouponViewedAnalytics,
  buildCouponClaimAnalytics,
  buildLastSearchedViewedAnalytics,
  buildLastSearchedClickedAnalytics,
  buildFeaturedDestinationViewedAnalytics,
  buildFeaturedDestinationClickedAnalytics,
  buildHotelDealsViewedAnalytics,
  buildHotelDealsClickedAnalytics,
  buildChinaDisclaimerViewedAnalytics,
} from './searchWidget.analytics';
import type { Suggestions } from '../../types';
import {
  BannerViewParams,
  BannerTapParams,
  ChinaDisclaimerViewedParams,
  CouponClaimParams,
  CouponViewedParams,
  FeaturedDestinationClickedParams,
  FeaturedDestinationViewedParams,
  HotelDealsClickedParams,
  HotelDealsViewedParams,
  LastSearchedClickedParams,
  LastSearchedViewedParams,
  SearchButtonParams,
} from './searchWidget.meta';

/**
 * Hotel Banner Events
 */

/**
 * Hotel banner view event - fires when a banner becomes visible in the carousel
 * @param params - Banner view parameters (bannerName, position)
 * @returns Analytics parameters
 */
export const hotelBannerViewEvent = (params: BannerViewParams) => {
  return buildHotelBannerViewAnalytics(params);
};

/**
 * Hotel banner tap event - fires when a user taps/clicks a banner
 * @param params - Banner tap parameters (bannerName, position)
 * @returns Analytics parameters
 */
export const hotelBannerTapEvent = (params: BannerTapParams) => {
  return buildHotelBannerTapAnalytics(params);
};

/**
 * Search Widget Events
 */

/**
 * Search widget view event - fires when user sees the search widget page
 * @returns Analytics parameters
 */
export const searchWidgetViewEvent = () => {
  return buildSearchWidgetViewAnalytics();
};

/**
 * Smart fill input event - fires when user types in smart fill search
 * @param userQuery - The user's search input
 * @param provider - The search provider (TypeSense or ElasticSearch)
 * @returns Analytics parameters
 */
export const smartFillInputEvent = (userQuery: string, provider: string) => {
  return buildSmartFillInputAnalytics(userQuery, provider);
};

/**
 * Suggestion click event - fires when user clicks on a smart fill suggestion
 * @param stationName - The selected station name
 * @param position - The position of the suggestion in the list
 * @returns Analytics parameters
 */
export const suggestionClickEvent = (stationName: string, position: number) => {
  return buildSuggestionClickAnalytics(stationName, position);
};

export const popularSearchClickEvent = (
  stationName: string,
  position: number,
  searchterm: string | null,
  destinationcategory: string,
  location_id: string,
) => {
  return buildPopularSearchClickAnalytics(
    stationName,
    position,
    searchterm,
    destinationcategory,
    location_id,
  );
};

export const popularSearchViewedEvent = (
  items: Array<{
    item_id: string;
    item_name: string;
    item_category: string;
    index: number;
  }>,
) => {
  return buildPopularSearchViewedAnalytics(items);
};

/**
 * No results event - fires when search returns no results
 * @param userQuery - The user's search query that returned no results
 * @returns Analytics parameters
 */
export const noResultsEvent = (userQuery: string) => {
  return buildNoResultsAnalytics(userQuery);
};

/**
 * Search button click event - fires when user clicks the search button
 * @param params - Search button click parameters
 * @returns Analytics parameters
 */
export const searchButtonClickEvent = (params: SearchButtonParams) => {
  return buildSearchButtonClickAnalytics(params);
};

/**
 * Coupon claim event - fires when user claims a coupon from the carousel
 * @param params - Coupon claim parameters
 * @returns Analytics parameters
 */
export const couponClaimEvent = (params: CouponClaimParams) => {
  return buildCouponClaimAnalytics(params);
};

export const couponViewedEvent = (params: CouponViewedParams) => {
  return buildCouponViewedAnalytics(params);
};

/**
 * Last searched viewed event - fires when last searched results are exposed to user
 * @param params - Last searched viewed parameters
 * @returns Analytics parameters
 */
export const lastSearchedViewedEvent = (params: LastSearchedViewedParams) => {
  return buildLastSearchedViewedAnalytics(params);
};

/**
 * Last searched clicked event - fires when user clicks on a hotel from last searched results
 * @param params - Last searched clicked parameters
 * @returns Analytics parameters
 */
export const lastSearchedClickedEvent = (params: LastSearchedClickedParams) => {
  return buildLastSearchedClickedAnalytics(params);
};

/**
 * Featured destination viewed event - fires when featured destinations are exposed to user
 * @param params - Featured destination viewed parameters
 * @returns Analytics parameters
 */
export const featuredDestinationViewedEvent = (
  params: FeaturedDestinationViewedParams,
) => {
  return buildFeaturedDestinationViewedAnalytics(params);
};

/**
 * Featured destination clicked event - fires when user clicks on a featured destination
 * @param params - Featured destination clicked parameters
 * @returns Analytics parameters
 */
export const featuredDestinationClickedEvent = (
  params: FeaturedDestinationClickedParams,
) => {
  return buildFeaturedDestinationClickedAnalytics(params);
};

/**
 * Hotel deals viewed event - fires when hotel deals recommendation listing is exposed to user
 * @param params - Hotel deals viewed parameters
 * @returns Analytics parameters
 */
export const hotelDealsViewedEvent = (params: HotelDealsViewedParams) => {
  return buildHotelDealsViewedAnalytics(params);
};

/**
 * Hotel deals clicked event - fires when user taps on hotel deals recommendation listing
 * @param params - Hotel deals clicked parameters
 * @returns Analytics parameters
 */
export const hotelDealsClickedEvent = (params: HotelDealsClickedParams) => {
  return buildHotelDealsClickedAnalytics(params);
};

/**
 * China disclaimer viewed event - fires when user views China disclaimer message
 * @param params - China disclaimer viewed parameters
 * @returns Analytics parameters
 */
export const chinaDisclaimerViewedEvent = (
  params: ChinaDisclaimerViewedParams,
) => {
  return buildChinaDisclaimerViewedAnalytics(params);
};

/**
 * Smart fill results event - fires when smart fill suggestions are returned
 * @param suggestions - Array of suggestions from smart fill response
 * @returns Analytics parameters
 */
export const smartFillResultsEvent = (suggestions: Suggestions[]) => {
  return buildSmartFillResultsAnalytics(suggestions);
};
